import { matchPath, Route, Routes, useLocation } from "react-router";
import React, { Suspense, lazy, FC, useMemo, useLayoutEffect } from "react";
import Loading from "../../utils/Loading";
import { useAppSelector } from "config/hooks";
import SignIn from "../../Pages/SignIn";
import ForgotPassword from "../../Pages/ForgotPassword";
import ResetPassword from "../../Pages/ResetPassword";
import Invitation from "../../Pages/Invitation";
import ActionItemLink from "../../Pages/ActionItems/ActionItemLink";
import PendingInvitationsPrompt from "components/PendingInvitationsPrompt";
import NoAccountAccess from "../../Pages/NoAccountAccess";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { isNil } from "lodash";
import wowzaRecordings from "api/wowzaRecordings";

const Domains = lazy(() => import("../../Pages/Domains"));
const ControlRoom = lazy(() => import("../../Pages/ControlRoom"));
const Tools = lazy(() => import("../../Pages/Tools"));
const Content = lazy(() => import("../../Pages/Content"));
const Series = lazy(() => import("../../Pages/Series"));
const Logs = lazy(() => import("../../Pages/Logs"));
const Users = lazy(() => import("../../Pages/Users"));
const Transcoders = lazy(() => import("../../Pages/Transcoders"));
const StreamTargets = lazy(() => import("../../Pages/StreamTargets"));
const Account = lazy(() => import("../../Pages/Account"));
const Contracts = lazy(() => import("../../Pages/Contracts"));
const Venues = lazy(() => import("../../Pages/Venues"));
const Home = lazy(() => import("../../Pages/Home"));
const Passes = lazy(() => import("../../Pages/Passes"));
const FullAccess = lazy(() => import("../../Pages/FullAccess"));
const FreeAccess = lazy(() => import("../../Pages/FreeAccess"));
const Coupons = lazy(() => import("../../Pages/Coupons"));
const Payments = lazy(() => import("../../Pages/Payments"));
const Marketing = lazy(() => import("../../Pages/Marketing"));
const Campaigns = lazy(() => import("../../Pages/Campaigns"));
const Donations = lazy(() => import("../../Pages/Donations"));
const Balance = lazy(() => import("../../Pages/Balance"));
const Sales = lazy(() => import("../../Pages/Sales"));
const Customers = lazy(() => import("../../Pages/Customers"));
const Bookmarks = lazy(() => import("../../Pages/Bookmarks"));
const Platform = lazy(() => import("../../Pages/Platform"));
const Announcements = lazy(() => import("../../Pages/Annoucements"));
const AnnouncementsPage = lazy(() => import("../../Pages/Announcements"));
const Recordings = lazy(() => import("../../Pages/Recordings"));
const Submissions = lazy(() => import("../../Pages/Submissions"));
const Guides = lazy(() => import("../../Pages/Guides"));
const GuidePage = lazy(() => import("../../Pages/Guides/Page"));
const ProductionTraining = lazy(() => import("../../Pages/Guides/Page/ProductionTraining"));
const Tags = lazy(() => import("../../Pages/Tags"));
const WowzaRecordings = lazy(() => import("../../Pages/WowzaRecordings"));
const AdminRecordings = lazy(() => import("../../Pages/Admin/Recordings"));
const AdminAppTest = lazy(() => import("Pages/Admin/AppTest"));
const Analytics = lazy(() => import("../../Pages/Analytics"));
const Reports = lazy(() => import("../../Pages/Reports"));
const AdminAccounts = lazy(() => import("../../Pages/Admin/Accounts"));
const AdminUsers = lazy(() => import("../../Pages/Admin/Users"));
const AdminCronLogs = lazy(() => import("../../Pages/Admin/CronLogs"));
const AdminEventMessages = lazy(() => import("../../Pages/Admin/EventMessages"));
const AdminOffSeasonPasses = lazy(() => import("../../Pages/Admin/OffSeasonPasses"));
const AdminActionItems = lazy(() => import("../../Pages/Admin/ActionItems"));
const AdminSettings = lazy(() => import("../../Pages/Admin/Settings"));
const AdminVideos = lazy(() => import("../../Pages/Admin/Videos"));
const AdminDomains = lazy(() => import("../../Pages/Admin/Domains"));
const AdminVerticals = lazy(() => import("../../Pages/Admin/Verticals"));
const AdminPayments = lazy(() => import("../../Pages/Admin/Payments"));
const AdminLanding = lazy(() => import("../../Pages/Admin/Landing"));
const ActionItems = lazy(() => import("../../Pages/ActionItems"));

const AppMain: FC = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
  }, [location.pathname]);
  const accounts = useAppSelector((state) => state.auth.accounts);
  const isSignedIn = useAppSelector((state) => state.auth.isSignedIn);
  const user = useAppSelector((state) => state.auth.currentUser?.data);
  const currentAccount = useAppSelector((state) => {
    const { currentAccount: id, accounts: list } = state.auth;
    const index = list.findIndex((a: any) => a.id === id);
    return index === -1 ? list[0] : list[index];
  });

  if (!isSignedIn) {
    const path = location.pathname.replace(/\/+$/, "");
    if (path === "/forgot-password") {
      return <ForgotPassword />;
    }
    if (path === "/reset-password") {
      return <ResetPassword />;
    }
    if (matchPath("/invitation/:key", path)) {
      return (
        <Routes>
          <Route path="/invitation/:key" element={<Invitation />} />
        </Routes>
      );
    }
    // Emailed action item link: no Riivet login needed (RM-3943).
    if (matchPath("/action-items/link/:token", path)) {
      return (
        <Routes>
          <Route path="/action-items/link/:token" element={<ActionItemLink />} />
        </Routes>
      );
    }
    return <SignIn />;
  }

  if ((accounts.length > 0 && isNil(currentAccount)) || isNil(user)) {
    return <Loading />; 
  }

  const isPlatformUser: boolean = user.roles.length > 0;
  const isInvitationLink =
    matchPath("/invitation/:key", location.pathname) ||
    matchPath("/action-items/link/:token", location.pathname);

  if (accounts.length === 0 && !isPlatformUser && !isInvitationLink) {
    return (<>
      <NoAccountAccess user={user} />
      <PendingInvitationsPrompt userEmail={user.email} />
      <ToastContainer autoClose={2000} />
    </>);
  }

  return (<>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/invitation/:key" element={<Invitation />} />
          <Route path="/action-items/link/:token" element={<ActionItemLink />} />
          <Route path="/action-items/*" element={<ActionItems />} />
          {/* PRODUCTION */}
          <Route path="/control-room/*" element={<ControlRoom />} />
          <Route path="/tools/*" element={<Tools />} />
          {/* CONTENT */}
          <Route path="/content/*" element={<Content />} />
          <Route path="/shows/*" element={<Series />} />
          <Route path="/bookmarks/*" element={<Bookmarks />} />
          <Route path="/passes/*" element={<Passes />} />
          <Route path="/tags/*" element={<Tags />} />
          {/* MARKETING */}
          <Route path="/campaigns/*" element={<Campaigns />} />
          <Route path="/marketing/*" element={<Marketing />} />
          <Route path="/analytics/*" element={<Analytics />} />
          {/* DONATIONS */}
          <Route path="/donations/*" element={<Donations />} />
          {/* FINANCIAL */}
          <Route path="/sales/*" element={<Sales />} />
          <Route path="/payments/*" element={<Payments />} />
          <Route path="/balance/*" element={<Balance />} />
          <Route path="/coupons/*" element={<Coupons />} />
          <Route path="/full-access/*" element={<FullAccess />} />
          <Route path="/free-access/*" element={<FreeAccess />} />
          <Route path="/customers/*" element={<Customers />} />
          <Route path="/contracts/*" element={<Contracts />} />
          {/* CONFIGURATION */}
          <Route path="/domains/*" element={<Domains />} />
          <Route path="/transcoders/*" element={<Transcoders />} />
          <Route path="/stream_targets/*" element={<StreamTargets />} />
          <Route path="/users/*" element={<Users />} />
          <Route path="/logs/*" element={<Logs />} />
          {/* PLATFORM */}
          {isPlatformUser && (<>
            <Route path="/platform" element={<AdminLanding />} />
            <Route path="/platform/announcements/*" element={<Announcements />} />
            <Route path="/platform/submissions/*" element={<Submissions />} />
            <Route path="/platform/devops/*" element={<Platform />} />
            <Route path="/platform/wowza-recordings/*" element={<WowzaRecordings />} />
            <Route path="/platform/recordings/*" element={<AdminRecordings />} />
            <Route path="/platform/apps/*" element={<AdminAppTest />} />
            <Route path="/platform/reports/*" element={<Reports />} />
            <Route path="/platform/accounts/*" element={<AdminAccounts />} />
            <Route path="/platform/users/*" element={<AdminUsers />} />
            <Route path="/platform/cron-logs/*" element={<AdminCronLogs />} />
            <Route path="/platform/event-messages/*" element={<AdminEventMessages />} />
            <Route path="/platform/off-season-passes/*" element={<AdminOffSeasonPasses />} />
            <Route path="/platform/action-items/*" element={<AdminActionItems />} />
            <Route path="/platform/settings/*" element={<AdminSettings />} />
            <Route path="/platform/videos/*" element={<AdminVideos />} />
            <Route path="/platform/domains/*" element={<AdminDomains />} />
            <Route path="/platform/verticals/*" element={<AdminVerticals />} />
            <Route path="/platform/payments/*" element={<AdminPayments />} />
            <Route path="/platform/venues/*" element={<Venues />} />
            <Route path="/platform/guides/*" element={<Guides />} />
          </>)}
          {/* OTHERS */}
          <Route path="/announcements" element={<AnnouncementsPage />} />
          <Route path="/recordings/*" element={<Recordings />} />
          <Route path="/account/*" element={<Account />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/guide/production-training/*" element={<ProductionTraining />} />
        </Routes>
      </Suspense>
      {!matchPath("/invitation/:key", location.pathname) && <PendingInvitationsPrompt userEmail={user.email} />}
      <ToastContainer autoClose={2000} />
  </>);
};

export default AppMain;
